import React, { useEffect, useRef } from "react";
import cn from "classnames";
import styles from "../styles.modules.scss";
import { FormattedMessage } from "react-intl";
import { Button } from "../../../button";
import { Image } from "../../../image";
import { useInboxRewardsContext } from "../../../../stores/inboxRewards";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { usePollContext } from "../../../../stores/poll";

export type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    background?: ImageSourceSets;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting?: Targeting;
    lang: Locale;
};

const calculateMilestonePosition = (
    progress: number,
    index: number,
    milestoneCount: number,
) => {
    return Math.max(
        index === 0
            ? progress - 2
            : progress - 2 + (2 / (milestoneCount - 1)) * index,
        5, // 5% is the minimum we support without the UI looking totally wack
    );
};

const calculateProgressPercentage = (
    progress: number,
    milestones: ProgressMilestone[],
) => {
    const firstProgress = milestones[0]?.progress;

    if (!firstProgress) {
        return progress;
    }

    const firstFactor = (firstProgress - 2) / firstProgress;
    const restFactor = (100 - (firstProgress - 2)) / (100 - firstProgress);

    return progress <= firstProgress
        ? progress * firstFactor
        : firstProgress - 2 + (progress - firstProgress) * restFactor;
};

const stopTouchMovePropagation = (e: TouchEvent) => {
    const target = e.currentTarget as HTMLElement;

    const isScrollable =
        target.scrollWidth > target.clientWidth ||
        target.scrollHeight > target.clientHeight;

    // Prevent touch from bubbling if inner div can scroll
    if (isScrollable) {
        e.stopPropagation();
    }
};

function scrollToWithEasing(
    element: HTMLElement,
    targetX: number,
    duration: number = 500,
) {
    const startX = element.scrollLeft;
    const distance = targetX - startX;
    const startTime = performance.now();

    const easeOutQuad = (t: number) => t * (2 - t);

    function animate(currentTime: number) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1); // clamp to [0,1]

        element.scrollLeft = startX + distance * easeOutQuad(progress);

        if (progress < 1) {
            requestAnimationFrame(animate);
        }
    }

    requestAnimationFrame(animate);
}

export const HastyHedgehogProgressSlide = ({
    slide,
}: {
    slide: ProgressSlideType;
}) => {
    const { title, tracker, milestones, background, targeting } = slide;

    const { actions } = useInboxRewardsContext();

    const claimedVouchers = actions.getClaimedVouchers();

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>();
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const {
        actions: { openModal },
    } = usePollContext();
    const firstLockedRef = useRef<HTMLDivElement>(null);
    const trackerProgressRef = useRef<HTMLDivElement>(null);
    const trackerRef = useRef<HTMLDivElement>(null);

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const ctas = parseCTAs(slide.ctas, handleClick);

    const fillPercentage = calculateProgressPercentage(
        tracker.progress,
        milestones,
    );

    const direction = tracker.direction;
    const barColor = tracker.color;

    useEffect(() => {
        const availableVouchers = milestones.filter((m) => !!m.voucherId);

        if (!availableVouchers.length) {
            return;
        }

        const voucherIds = availableVouchers.map(
            (m) => m.voucherId,
        ) as string[];

        actions.fetchUnclaimedVouchers(voucherIds);
    }, []);

    useEffect(() => {
        const el = trackerRef.current;
        if (!el) return;

        el.addEventListener("touchmove", stopTouchMovePropagation, {
            passive: false,
        });

        return () => {
            el.removeEventListener("touchmove", stopTouchMovePropagation);
        };
    }, []);

    useEffect(() => {
        if (isVisible) {
            if (slide.lang === "ar") {
                trackerRef.current?.scrollTo({
                    left:
                        (trackerRef.current.scrollWidth -
                            trackerRef.current.clientWidth) *
                        -1,
                });
            } else {
                trackerRef.current?.scrollTo({
                    left: 0,
                });
            }
        }
    }, [isVisible]);

    useEffect(() => {
        if (slide.lang !== "ar") {
            setTimeout(() => {
                if (firstLockedRef.current && isVisible) {
                    const left =
                        (firstLockedRef.current?.offsetLeft ?? 0) +
                        (firstLockedRef.current?.clientWidth ?? 0) / 2 -
                        (trackerRef.current?.clientWidth ?? 0) / 2;

                    trackerRef.current &&
                        scrollToWithEasing(trackerRef.current, left, 500);
                }
            }, 500);
        }
    }, [firstLockedRef.current, isVisible]);

    const style =
        direction === "vertical"
            ? {
                  height: `${fillPercentage}%`,
              }
            : {
                  width: `${fillPercentage}%`,
              };

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const barColorStyle = { "--bar-color": barColor } as React.CSSProperties;

    const aspectRatio = window.innerHeight / window.innerWidth;

    const milestonePlusRewards = [
        { text: "3x" },
        { text: "3x" },
        { text: "3x" },
        { text: "3x" },
        { text: "3x" },
        { text: "100x" },
        { text: "3x" },
        { text: "10x" },
    ];

    return (
        <div
            className={`slide progress-indicator ${styles.slide} ${
                isVisible ? "visible" : ""
            } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                targeting?.campaignId ?? ""
            } ${title ? "with-title" : "without-title"} locale-${slide.lang}`}
            ref={ref}
            style={barColorStyle}
        >
            {title && (
                <>
                    <div className={`filter ${styles.filter}`} />

                    <div
                        className={`title ${styles.title} title-${slide.lang}`}
                        data-title={title}
                    >
                        {title}
                    </div>
                </>
            )}

            <div className={`content ${styles.content}`}>
                {background && (
                    <div
                        className={`image slide-bg-image ${styles.image}`}
                    ></div>
                )}

                <div className="tracker-swiper" ref={trackerRef}>
                    <div
                        className="tracker-slide"
                        style={{ width: `${milestones.length * 11}rem` }}
                    >
                        <div
                            className={cn(`trackers`, styles.trackers, {
                                tablet: aspectRatio > 0.666,
                            })}
                            style={{ width: `${milestones.length * 11}rem` }}
                        >
                            <div className={`tracker-wrapper`}>
                                <div className={`tracker ${styles.tracker}`}>
                                    {isVisible && (
                                        <div
                                            style={style}
                                            className={`tracker-progress ${
                                                styles["tracker-progress"]
                                            } ${
                                                fillPercentage > 99
                                                    ? "almost-complete"
                                                    : ""
                                            } `}
                                            ref={trackerProgressRef}
                                        />
                                    )}
                                </div>

                                {milestones.map((milestone, index) => {
                                    const milestonePosition =
                                        calculateMilestonePosition(
                                            milestone.progress,
                                            index,
                                            milestones.length,
                                        );

                                    const style =
                                        direction === "vertical"
                                            ? {
                                                  bottom: `${milestonePosition}%`,
                                              }
                                            : {
                                                  left: `${milestonePosition}%`,
                                              };

                                    return (
                                        <span
                                            key={index}
                                            style={style}
                                            className="milestone-marker"
                                        />
                                    );
                                })}
                            </div>

                            <div
                                className={`milestone-wrapper ${styles["milestone-wrapper"]} milestones-${milestones.length}`}
                            >
                                {milestones.map((milestone, index) => {
                                    if (!milestone.image) {
                                        return null;
                                    }

                                    const milestonePosition =
                                        calculateMilestonePosition(
                                            milestone.progress,
                                            index,
                                            milestones.length,
                                        );

                                    const hasReachedMilestone =
                                        milestone.unlocked;

                                    const style =
                                        direction === "vertical"
                                            ? {
                                                  bottom: `${milestonePosition}%`,
                                              }
                                            : {
                                                  left: `${milestonePosition}%`,
                                              };

                                    const isClaimable = !!milestone.voucherId;

                                    const isClaimed = claimedVouchers.includes(
                                        milestone.voucherId as string,
                                    );

                                    const firstLockedProgress =
                                        milestones.find(
                                            (milestone) => !milestone.unlocked,
                                        )?.progress ?? tracker.progress;

                                    return (
                                        <div
                                            onClick={() => {
                                                if (isClaimed) {
                                                    return;
                                                }

                                                if (hasReachedMilestone) {
                                                    sendCommand(
                                                        "GAME_COMMAND",
                                                        {
                                                            url: "hayday-inbox://OpenDiamondShop",
                                                            id: `milestone-chronos-${milestone.eventId}`,
                                                            label: "OpenShopOffer",
                                                        },
                                                    );
                                                }

                                                if (!milestone.voucherId) {
                                                    return;
                                                }

                                                actions.claimVoucher(
                                                    milestone.voucherId,
                                                );
                                            }}
                                            style={style}
                                            key={index}
                                            ref={
                                                milestone.progress ===
                                                firstLockedProgress
                                                    ? firstLockedRef
                                                    : null
                                            }
                                            className={cn(
                                                "milestone-img",
                                                styles["milestone-img"],
                                                {
                                                    "milestone-achieved":
                                                        isClaimable
                                                            ? hasReachedMilestone &&
                                                              isClaimed
                                                            : hasReachedMilestone,
                                                },

                                                {
                                                    "milestone-unachieved":
                                                        !hasReachedMilestone,
                                                },

                                                isClaimable
                                                    ? hasReachedMilestone &&
                                                          isClaimed
                                                    : hasReachedMilestone,
                                                {
                                                    "milestone-claimable":
                                                        !isClaimed &&
                                                        isClaimable,
                                                },

                                                !isClaimed && isClaimable
                                                    ? styles[
                                                          "milestone-claimable"
                                                      ]
                                                    : undefined,
                                            )}
                                        >
                                            <Image sources={milestone.image} />

                                            {!hasReachedMilestone && (
                                                <div className="milestone-locked" />
                                            )}
                                            {hasReachedMilestone && (
                                                <div className="milestone-unlocked" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div
                                className={`milestone-plus-reward-wrapper milestones-${milestones.length}`}
                            >
                                {milestones.map((milestone, index) => {
                                    if (!milestone.image) {
                                        return null;
                                    }

                                    const milestonePlusReward =
                                        milestonePlusRewards[index] ?? {
                                            text: "",
                                            variant: "",
                                        };

                                    const milestonePosition =
                                        calculateMilestonePosition(
                                            milestone.progress,
                                            index,
                                            milestones.length,
                                        );

                                    const hasReachedMilestone =
                                        milestone.unlocked;

                                    const style =
                                        direction === "vertical"
                                            ? {
                                                  bottom: `${milestonePosition}%`,
                                              }
                                            : {
                                                  left: `${milestonePosition}%`,
                                              };

                                    const isClaimable = !!milestone.voucherId;

                                    const isClaimed = claimedVouchers.includes(
                                        milestone.voucherId as string,
                                    );

                                    return (
                                        <div
                                            onClick={() => {
                                                if (isClaimed) {
                                                    return;
                                                }

                                                if (hasReachedMilestone) {
                                                    sendCommand(
                                                        "GAME_COMMAND",
                                                        {
                                                            url: "hayday-inbox://OpenDiamondShop",
                                                            id: `milestone-chronos-${milestone.eventId}`,
                                                            label: "OpenShopOffer",
                                                        },
                                                    );
                                                }

                                                if (!milestone.voucherId) {
                                                    return;
                                                }

                                                actions.claimVoucher(
                                                    milestone.voucherId,
                                                );
                                            }}
                                            style={style}
                                            key={index}
                                            className={cn(
                                                "milestone-plus-reward-img",
                                                `milestone-plus-reward-img-${slide.lang}`,
                                                `plus-reward-${index + 1}`,
                                                {
                                                    "milestone-achieved":
                                                        isClaimable
                                                            ? hasReachedMilestone &&
                                                              isClaimed
                                                            : hasReachedMilestone,
                                                },

                                                {
                                                    "milestone-unachieved":
                                                        !hasReachedMilestone,
                                                },

                                                isClaimable
                                                    ? hasReachedMilestone &&
                                                          isClaimed
                                                    : hasReachedMilestone,
                                                {
                                                    "milestone-claimable":
                                                        !isClaimed &&
                                                        isClaimable,
                                                },

                                                !isClaimed && isClaimable
                                                    ? styles[
                                                          "milestone-claimable"
                                                      ]
                                                    : undefined,
                                            )}
                                        >
                                            <div className="milestone-plus-reward-text">
                                                {milestonePlusReward.text}
                                            </div>
                                            <div
                                                className={`milestone-plus-reward-image`}
                                            ></div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div
                                className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                            >
                                {milestones.map((milestone, index) => {
                                    if (!milestone.label) {
                                        return null;
                                    }

                                    const milestonePosition =
                                        calculateMilestonePosition(
                                            milestone.progress,
                                            index,
                                            milestones.length,
                                        );

                                    const hasReachedMilestone =
                                        milestone.unlocked;

                                    const style =
                                        direction === "vertical"
                                            ? {
                                                  bottom: `${milestonePosition}%`,
                                              }
                                            : {
                                                  left: `${milestonePosition}%`,
                                              };

                                    return (
                                        <div
                                            key={index}
                                            style={style}
                                            className={`milestone-label milestone-label-${slide.lang} ${
                                                styles["milestone-label"]
                                            } ${
                                                hasReachedMilestone
                                                    ? styles[
                                                          "milestone-reached"
                                                      ]
                                                    : styles[
                                                          "milestone-unreached"
                                                      ]
                                            } ${
                                                hasReachedMilestone
                                                    ? "milestone-reached"
                                                    : "milestone-unreached"
                                            }`}
                                            title={milestone.label}
                                        >
                                            {milestone.label}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`wrapper ${styles.wrapper}`}>
                    {ctas.length ? (
                        <div
                            className={`ctas ${
                                ctas.length === 1 ? "single" : "multi"
                            } ${styles.ctas}`}
                        >
                            {ctas.map(({ label, variant, onClick }, index) => {
                                return (
                                    <Button
                                        key={index}
                                        onClick={onClick}
                                        variant={
                                            variant === "green"
                                                ? "hot-pink"
                                                : variant === "blue"
                                                  ? "indigo-blue"
                                                  : variant
                                        }
                                    >
                                        <span className="buttonTextWrapper">
                                            {label || (
                                                <FormattedMessage id="globalText.langGo" />
                                            )}
                                        </span>
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
